<template>
  <BaseTopBar v-if="isMounted" v-hotkey="shortcuts" is-hideable>
    <template #left>
      <Breadcrumb
        v-if="hierarchy?.length || database?.workspace"
        :items="hierarchy || []"
        :home="breadcrumbHome"
      />
      <div v-if="showDatepicker" class="datepicker-container">
        <span class="date-separator" />
        <p ref="dateEl" class="date-display" @click.stop="handleDateClick">
          {{ dateDisplay }}
        </p>
        <div class="date-nav">
          <BaseButton
            kind="auxiliary"
            type="secondary"
            tooltip="Previous day"
            tooltip-display="always"
            @click.stop="$emit('addDay', -1)"
          >
            <template #icon>
              <ChevronLeftIcon />
            </template>
          </BaseButton>
          <BaseButton
            kind="auxiliary"
            type="secondary"
            tooltip="Next day"
            tooltip-display="always"
            @click.stop="$emit('addDay', 1)"
          >
            <template #icon>
              <ChevronRightIcon />
            </template>
          </BaseButton>
          <BaseButton
            v-if="!isSelectedDateToday"
            kind="auxiliary"
            type="secondary"
            tooltip="Today"
            tooltip-display="always"
            @click.stop="$emit('goToday')"
          >
            <template #icon>
              <BackIcon />
            </template>
          </BaseButton>
        </div>
      </div>
    </template>
    <template #right>
      <!-- Pin and Share, like CustomObjectTopBar's right side — hidden
           while editing (like the "..." menu) to keep the chrome clean. -->
      <template v-if="!isEditMode && isObjectLoaded">
        <BaseButton
          kind="auxiliary"
          :text="isPinned ? 'Unpin' : 'Pin'"
          @click="togglePin"
        >
          <template #icon>
            <PinIcon :class="{ pinned: isPinned }" />
          </template>
        </BaseButton>
        <ShareButton :root="shareRoot" :organization-id="organizationId" />
      </template>
    </template>
    <template v-if="!isLoading && !error && !isEditMode" #more>
      <!-- Open the underlying custom object: the object route redirects
           canvas rows back to the canvas screen unless asked raw. -->
      <BaseButton
        v-if="isObjectLoaded"
        class="context-action"
        kind="auxiliary"
        text="Open"
        is-expanded
        @click="openUnderlyingObject($event)"
      >
        <template #icon>
          <OpenIcon />
        </template>
      </BaseButton>
      <BaseButton
        class="context-action"
        kind="auxiliary"
        text="Edit"
        is-expanded
        @click="$emit('toggleEdit')"
      >
        <template #icon>
          <EditIcon />
        </template>
      </BaseButton>
      <!-- Common object actions (Copy link, Duplicate, Delete, ...), same
           entries as CustomObjectTopBar's overflow menu. -->
      <BaseButton
        v-for="action in moreActions"
        :key="action.text"
        class="context-action"
        kind="auxiliary"
        :text="action.text"
        :state="action.state || 'default'"
        :tint="action.tint || 'none'"
        is-expanded
        @click="getActionFunc(action, $event)"
      >
        <template #icon> <component :is="action.icon" /> </template
        ><template #right>
          {{ action.shortcutsDisplay }}
        </template>
      </BaseButton>
    </template>
  </BaseTopBar>
  <!-- Hidden until `showPicker` (position: fixed, so the teleported top
       bar does not affect it). Same shape as the Journal's. -->
  <DatePicker ref="datePickerRef" :modes="['date']" :show-clear="false" />
</template>

<script lang="ts">
import { format, getYear, isToday, startOfToday } from "date-fns";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  watch,
} from "vue";

import type { GraphDatabase } from "@ctrl/controller-rpc";

import { objectActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import Breadcrumb, {
  type BreadcrumbHome,
  type BreadcrumbItem,
} from "@/components/Breadcrumb.vue";
import type { ObjectContextActions } from "@/components/context-menu/context-actions";
import DatePicker from "@/components/DatePicker.vue";
import BackIcon from "@/components/icons/BackIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import OpenIcon from "@/components/icons/OpenIcon.vue";
import PinIcon from "@/components/icons/PinIcon.vue";
import * as inject from "@/components/inject";
import type { ExportRoot } from "@/components/marketplace/export";
import { getObjectShortcuts } from "@/components/object-shortcuts";
import ShareButton from "@/components/ShareButton.vue";
import type { Shortcuts } from "@/components/shortcuts";
import type { CustomObjectTarget } from "@/types";
import { makeMove } from "@/utils/move";
import { navigateBackward, navigateToCustomObject } from "@/utils/navigate";

import { RAW_OBJECT_QUERY } from "./useCanvases";

export default defineComponent({
  name: "CanvasTopBar",
  components: {
    BackIcon,
    BaseButton,
    BaseTopBar,
    Breadcrumb,
    DatePicker,
    ChevronLeftIcon,
    ChevronRightIcon,
    EditIcon,
    OpenIcon,
    PinIcon,
    ShareButton,
  },
  props: {
    database: { type: Object as PropType<GraphDatabase>, required: false },
    /** The canvas row: enables the common object actions in "...". */
    objectId: { type: String, required: false },
    /** The canvas's organization: drives the Share menu's packaging. */
    organizationId: { type: String, required: false },
    hierarchy: {
      type: Array as PropType<Array<BreadcrumbItem>>,
      required: false,
      default: () => [],
    },
    isEditMode: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    error: { type: String as PropType<string | null>, default: null },
    showDatepicker: { type: Boolean, default: false },
    selectedDate: { type: Date, default: () => new Date() },
  },
  emits: {
    toggleEdit: () => true,
    addDay: (_n: number) => true,
    goToday: () => true,
    selectDate: (_date: Date) => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const router = inject.router();
    const simpleModal = inject.simpleModal();
    const isModalShown = inject.isModalShown();
    const isInputFocused = inject.isInputFocused();
    const menu = inject.menu();
    const palette = inject.palette();
    const destinationPicker = inject.destinationPicker();
    const move = makeMove(controller, profile, logger, destinationPicker);

    // FIXME: Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));

    const breadcrumbHome = computed((): BreadcrumbHome | undefined => {
      const w = props.database?.workspace;
      return w ? { type: "workspace", id: w } : undefined;
    });

    const isSelectedDateToday = computed(() => isToday(props.selectedDate));

    const dateDisplay = computed(() => {
      const d = props.selectedDate;
      const day = d.getDate();
      const selectedYear = getYear(d);
      const todayYear = getYear(startOfToday());
      if (selectedYear !== todayYear) {
        return format(d, "LLLL d, yyyy");
      }
      return `${format(d, "LLLL")} ${day}`;
    });

    // Common object actions (consuming mode's "..." menu): the same generic
    // entries as CustomObjectTopBar's overflow, wired through
    // `getObjectShortcuts`. The canvas is a plain graph object, so Copy
    // link, Duplicate, Move, Run automation, and Delete all apply.
    const objectData = computed<CustomObjectTarget | null>(() => {
      if (!props.objectId || !props.database) return null;
      return {
        type: "object",
        row_id: props.objectId,
        table_id: props.database.id,
        workspace_id: props.database.workspace,
      };
    });

    const isObjectLoaded = computed(() => objectData.value !== null);

    // The full object id (workspace/table/row) required by the menu's pin
    // API. Loaded async; reloaded when the canvas row changes.
    const fullObjectId = ref<string | null>(null);
    const loadFullObjectId = async () => {
      const target = objectData.value;
      if (!target) return;
      let id: string | null;
      try {
        id = await controller.graph.object.id.make({
          workspace_id: target.workspace_id,
          table_id: target.table_id,
          row_id: target.row_id,
        });
      } catch {
        id = null;
      }
      // The object may have changed while awaiting; drop stale results.
      if (objectData.value?.row_id !== target.row_id) return;
      fullObjectId.value = id;
    };
    watch(objectData, () => void loadFullObjectId(), { immediate: true });

    const isPinned = computed(() =>
      fullObjectId.value ? menu.isItemPinned(fullObjectId.value) : false
    );

    const togglePin = async () => {
      if (!fullObjectId.value) await loadFullObjectId();
      if (!fullObjectId.value) return;
      await menu.toggleObjectPin(fullObjectId.value, "object");
    };

    // No `exportAction`: the Share menu offers Copy link and packaging, but
    // not Export — a canvas body is layout JSON, not exportable content.
    const shareRoot = computed<ExportRoot | null>(() => {
      if (!objectData.value) return null;
      return {
        kind: "object",
        target: objectData.value,
        organizationId: props.organizationId,
      };
    });

    const shortcutsConfig = computed(() => {
      if (!objectData.value) return null;
      return getObjectShortcuts(
        objectData.value,
        {
          controller,
          logger,
          profile,
          router,
          simpleModal,
          isModalShown,
          isInputFocused,
          menu,
          palette,
          move,
        },
        {
          // The canvas route no longer exists once deleted: leave first.
          beforeDeleteAction: async () => {
            await navigateBackward(router, menu);
          },
        }
      );
    });

    const moreActions = computed<ObjectContextActions[]>(() => {
      if (!objectData.value) return [];
      return [
        objectActions.runAutomation,
        objectActions.move,
        objectActions.copyLink,
        objectActions.duplicate,
        { ...objectActions.delete, tint: "red" },
      ];
    });

    const getActionFunc = (
      action: ObjectContextActions,
      mouseEvent?: MouseEvent
    ) => {
      shortcutsConfig.value?.getActionFunc(action, mouseEvent);
    };

    // Open the underlying custom object page (the canvas row itself). The
    // `raw` flag bypasses the object route's redirect back to this screen.
    const openUnderlyingObject = async (event?: MouseEvent) => {
      const target = objectData.value;
      if (!target || !target.workspace_id) return;
      await navigateToCustomObject(
        router,
        target.workspace_id,
        target.table_id,
        target.row_id,
        event,
        { [RAW_OBJECT_QUERY]: "1" }
      );
    };

    // Keyboard shortcuts for the same actions — disabled while editing (a
    // stray Delete/Duplicate mid-edit would be destructive and confusing).
    const shortcuts = computed<Shortcuts>(() =>
      props.isEditMode ? {} : (shortcutsConfig.value?.shortcuts ?? {})
    );

    // Clicking the date opens the shared DatePicker anchored to it (same
    // pattern as the Journal's top bar date).
    const dateEl = ref<HTMLElement>();
    const datePickerRef = ref<InstanceType<typeof DatePicker>>();
    const handleDateClick = () => {
      const picker = datePickerRef.value;
      if (!picker || !dateEl.value) return;
      // Toggle: the picker's outside-click close ignores its trigger, so
      // reclicking the date must close it explicitly.
      if (picker.isDisplayed) {
        picker.closePicker();
        return;
      }
      picker.showPicker(dateEl.value, props.selectedDate, (selection) => {
        if (selection?.mode === "date") emit("selectDate", selection.date);
      });
    };

    return {
      isMounted,
      breadcrumbHome,
      isSelectedDateToday,
      dateDisplay,
      dateEl,
      datePickerRef,
      handleDateClick,
      moreActions,
      getActionFunc,
      openUnderlyingObject,
      shortcuts,
      isObjectLoaded,
      isPinned,
      togglePin,
      shareRoot,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/context-actions.scss";

.datepicker-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 4px;
}

.date-separator {
  width: 1px;
  height: 16px;
  margin: 0 6px;
  @include themed() {
    background: t($grey-15);
  }
}

.date-display {
  @extend %font-body-regular;
  margin: 0;
  padding: 4px 6px;
  border-radius: $border-radius-m;
  white-space: nowrap;
  @include transition(background-color 100ms ease-in);
  @include themed() {
    color: t($grey-80);

    &:hover {
      background-color: t($bg-menu-hover);
    }
  }
}

.date-nav {
  display: flex;
  flex-direction: row;
  column-gap: 2px;
}
</style>
